import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../pages/Api';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

// --- Edit Profile Modal Component ---
const EditProfileModal = ({ admin, onClose }) => {
    
    const [formData, setFormData] = useState({ 
        name: admin.name || '', 
        mobileno: admin.mobileno || '',
        gender: admin.gender || '1'
    });

    const handleSubmit = async () => {
        if (formData.mobileno.length !== 10) {
            toast.warn("Please enter a valid 10-digit mobile number.");
            return;
        }
        try {
            const token = localStorage.getItem('access_token');
            await API.post('admin/update-profile/', formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success("Profile Updated Successfully!");
            onClose();
            window.location.reload();
        } catch (err) { 
            console.error(err); 
            toast.error("Update failed!"); 
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-5 md:p-8 rounded-3xl md:rounded-[40px] w-full max-w-sm shadow-2xl">
                <h2 className="text-xl font-black mb-6 text-gray-800">Edit Profile</h2>
                
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">Name</label>
                <input className="w-full p-3 mb-4 border border-gray-200 rounded-xl mt-1" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">Mobile Number</label>
                <input type="tel" maxLength="10" className="w-full p-3 mb-4 border border-gray-200 rounded-xl mt-1" value={formData.mobileno} onChange={(e) => setFormData({...formData, mobileno: e.target.value.replace(/\D/g, '')})} />
                
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">City</label>
                <input className="w-full p-3 mb-4 border border-gray-200 rounded-xl mt-1" placeholder="e.g. Pune" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />

                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">Gender</label>
                <select className="w-full p-3 mb-6 border border-gray-200 rounded-xl mt-1 bg-white" value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})}>
                    <option value="1">Male</option>
                    <option value="2">Female</option>
                    <option value="3">Other</option>
                </select>

                <button onClick={handleSubmit} className="w-full bg-[#64a106] text-white py-3 rounded-2xl font-black uppercase tracking-wider">Save Changes</button>
                <button onClick={onClose} className="w-full mt-3 text-gray-400 font-bold hover:text-gray-600">Cancel</button>
            </div>
        </div>
    );
};

// --- Security Modal Component ---
const SecurityModal = ({ onClose }) => {
    const [pass, setPass] = useState({ old_password: '', new_password: '' });
    
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const handlePasswordChange = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await API.post('admin/change-password/', pass, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success("Password Changed Successfully!");
            onClose();
        } catch (err) { toast.error("Error: Check your old password"); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-[40px] w-full max-w-sm shadow-2xl">
                <h2 className="text-xl font-black mb-6">Security & Password</h2>
                
                {/* Old Password Field */}
                <div className="relative mb-4">
                    <input 
                        type={showOld ? "text" : "password"} 
                        className="w-full p-3 border border-gray-200 rounded-xl" 
                        placeholder="Old Password" 
                        onChange={(e) => setPass({...pass, old_password: e.target.value})} 
                    />
                    <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-4 top-4 text-gray-400">
                        {showOld ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>

                {/* New Password Field */}
                <div className="relative mb-6">
                    <input 
                        type={showNew ? "text" : "password"} 
                        className="w-full p-3 border border-gray-200 rounded-xl" 
                        placeholder="New Password" 
                        onChange={(e) => setPass({...pass, new_password: e.target.value})} 
                    />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-4 text-gray-400">
                        {showNew ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>
                
                <button onClick={handlePasswordChange} className="w-full bg-blue-900 text-white py-3 rounded-xl font-bold">Update Password</button>
                <button onClick={onClose} className="w-full mt-2 text-gray-400 font-bold">Cancel</button>
            </div>
        </div>
    );
};

// --- Main AdminProfile Component (Main content logic remains same) ---
const AdminProfile = () => {
    const navigate = useNavigate();
    const [admin, setAdmin] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);

    useEffect(() => {
        const fetchAdminProfile = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const res = await API.get('admin/profile/', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setAdmin(res.data);
            } catch (err) { 
                console.error("Error:", err); 
                if (err.response?.status === 401) navigate('/login');
            }
        };
        fetchAdminProfile();
    }, [navigate]);

    if (!admin) return <div className="p-10 text-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8 animate-in fade-in duration-500">
            {/* Header, Left Card, and Right Section same as before */}
            <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="flex items-center text-[#64a106] font-bold hover:underline border-none bg-transparent cursor-pointer">
                    <span className="mr-2">←</span> Back
                </button>
                <h1 className="text-xl font-sans text-gray-400 uppercase tracking-[4px]">Admin Profile</h1>
            </div>

            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
                <div className="bg-white rounded-3xl md:rounded-[40px] shadow-2xl p-5 md:p-10 text-center border-b-[10px] border-[#64a106] self-start">
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto mb-6 bg-[#64a106] border-4 border-white shadow-lg flex items-center justify-center text-white text-4xl font-bold">
                        {admin.name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                    </div>
                    <h2 className="text-xl md:text-2xl font-black text-gray-800 mb-1">{admin.name}</h2>
                    <p className="text-gray-400 text-sm mb-6 font-medium">{admin.emailid}</p>
                    <div className="inline-block bg-[#64a106]/10 text-[#64a106] px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-10">Verified Member</div>
                    
                    <div className="text-left space-y-5 pt-6 border-t border-gray-100">
                        <div className="flex justify-between items-center"><span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Gender</span><span className="text-gray-800 font-black text-sm">{admin.gender === "1" ? "Male" : "Not Specified"}</span></div>
                        <div className="flex justify-between items-center"><span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Role</span><span className="text-gray-800 font-black text-sm">Admin</span></div>
                        <div className="flex justify-between items-center"><span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Location</span><span className="text-gray-800 font-black text-sm">{admin.location}</span></div>
                    </div>
                </div>

                <div className="md:col-span-2 space-y-8">
                    {/* Stats Card */}
                    <div className="bg-white rounded-[40px] shadow-xl p-10 border-l-[12px] border-blue-900">
                        <h3 className="text-lg font-black text-gray-800 mb-8 uppercase tracking-tighter flex items-center gap-3">
                            <span className="bg-blue-900 text-white p-2 rounded-xl text-xs shadow-md">🛡️</span> Admin Statistics
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                            <div className="bg-gray-50/50 p-8 rounded-[35px] border border-gray-100 shadow-inner">
                                <p className="text-[10px] uppercase font-black text-gray-400 mb-2 tracking-widest">Access Level</p>
                                <p className="text-3xl font-black text-blue-900 uppercase">Full Control</p>
                            </div>
                            <div className="bg-gray-50/50 p-8 rounded-[35px] border border-gray-100 shadow-inner">
                                <p className="text-[10px] uppercase font-black text-gray-400 mb-2 tracking-widest">System Status</p>
                                <p className="text-2xl font-black text-green-600 uppercase">Online</p>
                            </div>
                        </div>
                    </div>
                    
                    <button onClick={() => setIsEditModalOpen(true)} className="w-full flex items-center justify-between p-8 bg-white rounded-[40px] shadow-xl hover:bg-gray-50 transition-all border-none cursor-pointer">
                        <span className="text-base font-black text-gray-700">Edit Personal Profile</span>
                    </button>
                    <button onClick={() => setIsSecurityModalOpen(true)} className="w-full flex items-center justify-between p-8 bg-white rounded-[40px] shadow-xl hover:bg-gray-50 transition-all border-none cursor-pointer">
                        <span className="text-base font-black text-gray-700">Security & Password</span>
                    </button>
                </div>
            </div>

            {isEditModalOpen && <EditProfileModal admin={admin} onClose={() => setIsEditModalOpen(false)} />}
            {isSecurityModalOpen && <SecurityModal onClose={() => setIsSecurityModalOpen(false)} />}
        </div>
    );
};

export default AdminProfile;